/**
 * Touch pan/pinch on the 3D viewport. The UI owns gesture recognition and emits
 * camera intents; Lane 2's renderer subscribes via onCamera and never touches DOM
 * gesture code itself. touch-action:none on the viewport kills browser gestures,
 * so pan/pinch never fights scrolling (done-when e).
 */
export interface CameraIntent {
  type: 'pan' | 'pinch' | 'tap';
  dx: number; dy: number;      // CSS px since last event
  scale: number;               // pinch scale factor since last event
  x: number; y: number;        // tap position
}

export function attachViewportGestures(el: HTMLElement, onCamera: (i: CameraIntent) => void): () => void {
  const pts = new Map<number, { x: number; y: number }>();
  let lastDist = 0;
  let moved = 0;

  const dist = () => {
    const [a, b] = [...pts.values()];
    if (!a || !b) return 0;
    return Math.hypot(a.x - b.x, a.y - b.y);
  };

  const down = (e: PointerEvent) => {
    el.setPointerCapture(e.pointerId);
    pts.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (pts.size === 2) lastDist = dist();
    moved = 0;
  };
  const move = (e: PointerEvent) => {
    const prev = pts.get(e.pointerId);
    if (!prev) return;
    const cur = { x: e.clientX, y: e.clientY };
    const dx = cur.x - prev.x, dy = cur.y - prev.y;
    pts.set(e.pointerId, cur);
    moved += Math.abs(dx) + Math.abs(dy);
    if (pts.size === 1) {
      onCamera({ type: 'pan', dx, dy, scale: 1, x: cur.x, y: cur.y });
    } else if (pts.size === 2) {
      const d = dist();
      if (lastDist > 0) onCamera({ type: 'pinch', dx: 0, dy: 0, scale: d / lastDist, x: cur.x, y: cur.y });
      lastDist = d;
    }
  };
  const up = (e: PointerEvent) => {
    const wasSingle = pts.size === 1;
    pts.delete(e.pointerId);
    if (wasSingle && moved < 8) onCamera({ type: 'tap', dx: 0, dy: 0, scale: 1, x: e.clientX, y: e.clientY });
    if (pts.size === 2) lastDist = dist();
  };

  el.addEventListener('pointerdown', down);
  el.addEventListener('pointermove', move);
  el.addEventListener('pointerup', up);
  el.addEventListener('pointercancel', up);
  return () => {
    el.removeEventListener('pointerdown', down);
    el.removeEventListener('pointermove', move);
    el.removeEventListener('pointerup', up);
    el.removeEventListener('pointercancel', up);
  };
}
